import type {Dayjs} from 'dayjs';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import advancedFormat from 'dayjs/plugin/advancedFormat.js';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

import {isNumber, isString} from './index.js';
import type {ScheduleTime} from '@gl/lit/types/journey/schedule.js';
import type {
  DateInputString,
  HourAndMinuteString,
  ISOString,
} from '@gl/lit/types/general.js';

const CUSTOM_DATE_FORMATS = ['YYYY-MM-DD HH:mm:ss Z'];

export function getOrdinalDayOfMonth(day: number) {
  switch (day) {
    case 1:
    case 21:
    case 31:
      return `${day}st`;
    case 2:
    case 22:
      return `${day}nd`;
    case 3:
    case 23:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

export function formatRelativeDate(date: Date) {
  const relativeDate = dayjs(new Date(date));
  return relativeDate.fromNow();
}

export function convertToDateInputValue(
  value: string | Date | Dayjs
): DateInputString | '' {
  const date = dayjs(value);
  if (!date.isValid()) return '';
  return date.format('YYYY-MM-DD') as DateInputString;
}

export function convertToISOString(value: string | Date | Dayjs): ISOString {
  const date = dayjs(value);
  return date.format('YYYY-MM-DDTHH:mm');
}

export const convertToDatetimeLocalInputValue = convertToISOString;

export function convertToTimeInputValue(
  value: string | Date | Dayjs
): HourAndMinuteString | '' {
  const date = dayjs(value);
  if (!date.isValid()) return '';
  return date.format('HH:mm') as HourAndMinuteString;
}

export function setScheduleTime(value: Date | string | number): ScheduleTime {
  const date = dayjs(
    isString(value) || isNumber(value) ? new Date(value) : value
  );
  return date.format('YYYY-MM-DDTHH:mm') as ScheduleTime;
}

export function isDateInputString(value: unknown): value is DateInputString {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!isString(value)) return false;
  return regex.test(value);
}

export function isHourAndMinuteString(
  value: unknown
): value is HourAndMinuteString {
  const regex = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
  if (!isString(value)) return false;
  return regex.test(value);
}

export function isAfter(
  a: string | number | Date | dayjs.Dayjs | null,
  b: string | number | Date | dayjs.Dayjs | null,
  unit: dayjs.OpUnitType = 'seconds'
): boolean {
  return dayjs(a).isAfter(dayjs(b), unit);
}

export function isBefore(
  a: string | number | Date | dayjs.Dayjs | null,
  b: string | number | Date | dayjs.Dayjs | null,
  unit: dayjs.OpUnitType = 'seconds'
): boolean {
  return dayjs(a).isBefore(dayjs(b), unit);
}

export function getTimestampFromNow(includeMillisecond = true): string {
  const now = dayjs();
  return includeMillisecond
    ? now.format('HH:mm:ss:SSS')
    : now.format('HH:mm:ss');
}

export function formatDatetimeInTimezone(
  value: string | Date | Dayjs,
  timezone = 'UTC'
): string {
  return dayjs(value).tz(timezone).format('YYYY-MM-DDTHH:mm');
}

/**
 * Takes a date and generates a new date X days, months, etc. from now.
 */
export function getDateFromNow(type: 'day' | 'month' | 'year', value: number) {
  switch (type) {
    case 'year': {
      return new Date(new Date().setFullYear(new Date().getFullYear() + value));
    }

    default:
      throw 'Unimplemented';
  }
}

/**
 * Takes a date and generates a valid date sring to use with HTML
 * date inputs.
 */
export function createHTMLDateString(date: Date) {
  return date.toISOString().split('T')[0];
}

function findValidCustomFormatForTimestamp(timestamp: string) {
  return CUSTOM_DATE_FORMATS.map(format => dayjs(timestamp, format)).find(d =>
    d.isValid()
  );
}

export function formatDateAsString(
  date: dayjs.Dayjs,
  isUtc = false,
  wordy = true
): string {
  const dateFormat = wordy ? 'MMMM Do, YYYY' : 'MM/DD/YYYY';
  const tzFormat = isUtc ? '[UTC]' : 'z';
  return date.format(`${dateFormat}, hh:mm A (${tzFormat})`);
}

export function formatTimeStamp(
  timestamp: string,
  isUtc = false,
  wordy = true
): string {
  const dateJs = isUtc ? dayjs.utc(timestamp) : dayjs(timestamp);

  if (dateJs.isValid()) return formatDateAsString(dateJs, isUtc, wordy);

  const fallbackValidDateFormat = findValidCustomFormatForTimestamp(timestamp);

  if (fallbackValidDateFormat) {
    return formatDateAsString(fallbackValidDateFormat, isUtc, wordy);
  }

  return timestamp;
}
